import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import "../styles/leadform.scss";
import avgicn from "../assets/img/leadform/avgicn.svg";
import trusticn from "../assets/img/leadform/trusticn.svg";
import lock from "../assets/img/leadform/lock.svg";
import liveicn from "../assets/img/leadform/liveicn.svg";
import handshake from "../assets/img/leadform/handshake.svg";
import whitdigoarw from "../assets/img/leadform/whitdigoarw.svg";
import greydigonarw from "../assets/img/leadform/greydigonarw.svg";
import greendigonalarw from "../assets/img/leadform/greendigonalarw.svg";
import minusicn from "../assets/img/leadform/minusicn.svg";
import righttickicn from "../assets/img/leadform/righttickicn.svg";

// ── Utility ──────────────────────────────────────────────
function fmt(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}
function genTrackingId() {
  const now = new Date();
  const date =
    now.getFullYear().toString() +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  return (
    "KURJA-" + date + "-" + String(Math.floor(Math.random() * 9000) + 1000)
  );
}
// fmtNow removed because it was unused (avoids eslint no-unused-vars)

// ── CheckIcon ─────────────────────────────────────────────
function CheckIcon({ color = "#888" }) {
  return (
    <svg className="ck" viewBox="0 0 14 14">
      <circle cx="7" cy="7" r="7" fill={color} />
      <path
        d="M4 7l2 2 4-4"
        stroke="#fff"
        strokeWidth="1.4"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Hero ──────────────────────────────────────────────────
function Hero({ companyCount = 41 }) {
  return (
    <div className="leadform-head">
      <div className="leadform-topshorthead">
        <span className="dot" />
        {companyCount} companies joined this month
      </div>
      <h1>
        Stop overpaying for electricity.
        <br />
        <span>Start optimising with AI.</span>
      </h1>
      <p>
        Kimbal Urja's EMS platform gives industries real-time control, AI-driven
        market intelligence,
        <br /> and AI-assisted power procurement — starting completely free.
      </p>
    </div>
  );
}

// ── TrustBar ──────────────────────────────────────────────
function TrustBar() {
  const items = [
    {
      icon: avgicn,
      text: "Avg. <span>7-10%</span> electricity cost reduction",
    },
    { icon: "", text: "|" },
    { icon: trusticn, text: "Trusted by industries, data centres  utilities" },
    { icon: "", text: "|" },
    { icon: lock, text: "Secured Data : Utility compliant" },
    { icon: "", text: "|" },
    { icon: liveicn, text: "Live in under 48 hours" },
  ];
  return (
    <div className="trust-bar">
      {items.map((item, i) => {
        const isDivider = item.text === "|";

        return (
          <div
            className={`trust-item ${isDivider ? "trust-divider" : ""}`}
            key={i}
          >
            {item.icon && (
              <img src={item.icon} alt="icon" className="trust-icon" />
            )}

            <span
              className="trust-item-desc"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.text),
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── SavingsCalc ───────────────────────────────────────────
function SavingsCalc({ bill, rate, annual, onBillChange, onRateChange }) {
  const annualSavings = Math.round(bill * 12 * (rate / 100));

  let planCostMonthly, planCostAnnual;
  if (bill < 300000) {
    planCostMonthly = 850;
    planCostAnnual = 9180;
  } else if (bill < 1000000) {
    planCostMonthly = 6000;
    planCostAnnual = 64800;
  } else if (bill < 10000000) {
    planCostMonthly = 18000;
    planCostAnnual = 194400;
  } else {
    planCostMonthly = 50000;
    planCostAnnual = 540000;
  }

  const displayCost = annual ? planCostAnnual : planCostMonthly * 12;
  const roi = Math.round((annualSavings / displayCost) * 100);
  const paybackDays =
    annualSavings > 0 ? Math.round((displayCost / annualSavings) * 365) : 0;

  const starterSav = fmt(bill * 12 * (rate / 100) * 0.5);
  const growthSav = fmt(bill * 12 * (rate / 100) * 0.71);
  const proSav = fmt(bill * 12 * (rate / 100) * 1.07);
  const maxSav = fmt(bill * 12 * (rate / 100) * 1.71);

  return (
    <div className="calc-card">
      <div className="calc-head">
        <div>
          <div className="calc-title">Your savings calculator</div>
          <div className="calc-sub">
            Slide to your numbers — see your personalised savings estimate
            instantly
          </div>
        </div>
        {/* <div className="rec-pill">Recommended: {planLabel} plan</div> */}
        <div className="rec-pill">Save 10%</div>
      </div>
      <div className="calc-body">
        <div className="sliders-row">
          <div>
            <div className="slider-line-item">
              <div className="slider-label">
                <span>Monthly electricity bill</span>
                <span className="slider-val">{fmt(bill)}</span>
              </div>
              <input
                type="range"
                min="100000"
                max="100000000"
                step="500000"
                value={bill}
                onChange={(e) => onBillChange(parseInt(e.target.value))}
              />
              <div className="rate-hint to-invisbleondesk">
                this will be empty
              </div>
            </div>

            <div className="slideritems-card">
              <div className="calc-result">
                <div className="result-label">Annual gross savings</div>
                <div className="result-val">{fmt(annualSavings)}</div>
                <div className="result-note">before plan fee</div>
              </div>
              <div className="calc-result">
                <div className="result-label">Plan cost (annual)</div>
                <div className="result-val gold">{fmt(displayCost)}/yr</div>
                <div className="result-note">
                  {annual ? "at 10% annual discount" : "billed monthly"}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="slider-line-item">
              <div className="slider-label">
                <span>Expected savings rate</span>
                <span className="slider-val">{Math.round(rate)}%</span>
              </div>
              <input
                type="range"
                min="3"
                max="20"
                step="0.5"
                value={rate}
                onChange={(e) => onRateChange(parseFloat(e.target.value))}
              />
              <div className="rate-hint">
                Industry average is 7% · adjust based on your usage profile
              </div>
            </div>

            <div className="slideritems-card">
              <div className="calc-result">
                <div className="result-label">Estimated ROI</div>
                <div className="result-val dark">{roi}%</div>
                <div className="result-note">return on plan spend</div>
              </div>
              <div className="calc-result">
                <div className="result-label">Payback period</div>
                <div className="result-val dark">{paybackDays} days</div>
                <div className="result-note">to recover plan cost</div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="results-row">
          <div className="calc-result">
            <div className="result-label">Annual gross savings</div>
            <div className="result-val">{fmt(annualSavings)}</div>
            <div className="result-note">before plan fee</div>
          </div>
          <div className="calc-result">
            <div className="result-label">Plan cost (annual)</div>
            <div className="result-val gold">{fmt(displayCost)}/yr</div>
            <div className="result-note">{annual ? "at 10% annual discount" : "billed monthly"}</div>
          </div>
          <div className="calc-result">
            <div className="result-label">Estimated ROI</div>
            <div className="result-val dark">{roi}%</div>
            <div className="result-note">return on plan spend</div>
          </div>
          <div className="calc-result">
            <div className="result-label">Payback period</div>
            <div className="result-val dark">{paybackDays} days</div>
            <div className="result-note">to recover plan cost</div>
          </div>
        </div> */}
      </div>

      {/* Hidden savings outputs exposed via data attrs for plan cards */}
      <div style={{ display: "none" }}>
        <span id="rs-starter">{starterSav}</span>
        <span id="rs-growth">{growthSav}</span>
        <span id="rs-pro">{proSav}</span>
        <span id="rs-max">{maxSav}</span>
      </div>
    </div>
  );
}

// ── BillingToggle ─────────────────────────────────────────
function BillingToggle({ annual, onChange }) {
  return (
    <div className="toggle-row">
      <span className={`tlabel ${!annual ? "on" : ""}`}>Monthly</span>
      <label className="tog">
        <input
          type="checkbox"
          checked={annual}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="tsl" />
      </label>
      <span className={`tlabel ${annual ? "on" : ""}`}>
        Annual &nbsp;<span className="sbadge">Save 10%</span>
      </span>
    </div>
  );
}

// ── PlanCard ──────────────────────────────────────────────
function PlanCard({
  plan,
  annual,
  bill,
  rate,
  onTrialOpen,
  onDemoOpen,
  onCompareOpen,
}) {
  const annualSavings = bill * 12 * (rate / 100);

  const savings = {
    Starter: fmt(annualSavings * 0.5),
    Growth: fmt(annualSavings * 0.71),
    Pro: fmt(annualSavings * 1.07),
    Max: fmt(annualSavings * 1.71),
  };

  const configs = {
    Starter: {
      badge: "45-day free trial",
      badgeCls: "b-free",
      cardCls: "",
      priceMonthly: 0,
      priceAnnual: 0,
      sym: "₹",
      amt: annual ? "765" : "0",
      per: "/mo",
      note: annual
        ? "Billed ₹9,180/yr after trial"
        : "Free for 45 days · then ₹850/mo",
      ctaLabel: "Start free",
      ctaCls: "ct-s",
      tag: "Energy visibility",
      meter: "1 meter · 3 users · 3-mo data",
      sectionLabel: "What you get:",
      roiColor: "roi-s",
      features: [
        { text: "Real-time energy dashboard" },
        { text: "Consumption alerts & basic reports" },
        { text: "Meter data ingestion (MDM)" },
      ],
      checkColor: "#026061",
      footer: "No credit card · live in 48h · cancel anytime",
      extraCta: { label: "What's included?", action: "compare" },
    },
    Growth: {
      badge: "Bridge tier",
      badgeCls: "b-new",
      cardCls: "",
      amt: annual ? "5,400" : "6,000",
      sym: "₹",
      per: "/mo",
      saveTag: annual ? "Save ₹7,200" : null,
      priceFrom: true,
      flexNote: "Scales with meter count",
      note: annual
        ? "Billed ₹64,800/yr · saves ₹7,200"
        : "+₹1K/mo per extra meter",
      ctaLabel: "Book a demo",
      ctaCls: "ct-g",
      tag: "Multi-site intelligence",
      meter: "Up to 5 meters · 10 users · 12-mo data",
      sectionLabel: "What you get:",
      roiColor: "roi-m",
      features: [
        { text: "ABT & smart meter visualisation" },
        { text: "Multi-meter consolidation" },
        { text: "Peak demand analysis & tariff dashboard" },
        { text: "Load profiling & energy waste detection" },
        { text: "Advanced analytics & benchmarking" },
        { text: "IEX market prices — read-only view" },
      ],
      checkColor: "#026061",
      footer: "Ideal for 2–5 site operators · chat + email · 24h SLA",
      extraCta: { label: "What's included?", action: "compare" },
    },
    Pro: {
      badge: "Most popular · highest ROI",
      badgeCls: "b-hot",
      cardCls: "hot",
      amt: annual ? "16,200" : "18,000",
      sym: "₹",
      per: "/mo",
      saveTag: annual ? "Save ₹21,600" : null,
      priceFrom: true,
      flexNote: "Tailored to your meter portfolio",
      note: annual
        ? "Billed ₹1,94,400/yr · saves ₹21,600 · or value-share"
        : "₹18K–₹30K by meter count · or value-share",
      ctaLabel: "Book a demo",
      ctaCls: "ct-p",
      tag: "AI-powered energy intelligence",
      meter: "Up to 20 meters · unlimited users · 3-yr data",
      sectionLabel: "What you get:",
      roiColor: "roi-p",
      features: [
        { text: "Manual IEX bidding & market access" },
        { text: "AI price prediction", sub: "ML-based IEX market forecasting" },
        {
          text: "AI demand forecasting",
          sub: "Predict load patterns for optimal buying",
        },
        { text: "Market segment recommendation AI" },
        { text: "Power exchange integration" },
      ],
      checkColor: "#026061",
      footer: "Priority support · 8h SLA · dedicated CSM · live in 48h",
      extraCta: { label: "What's included?", action: "compare" },
    },
    Max: {
      badge: "Enterprise · white-glove",
      badgeCls: "b-ent",
      cardCls: "ent",
      amt: annual ? "45,000" : "50,000",
      sym: "₹",
      per: "/mo",
      saveTag: annual ? "Save ₹60,000" : null,
      priceFrom: true,
      flexNote: "Fully custom to your portfolio",
      note: annual
        ? "Billed ₹5,40,000/yr · saves ₹60,000 · or value-share"
        : "Custom portfolio pricing · or value-share",
      priceStarting: true,
      ctaLabel: "Book a demo",
      ctaCls: "ct-m",
      tag: "Autonomous optimisation + trading",
      meter: "Unlimited meters & users · unlimited data",
      sectionLabel: "What you get:",
      roiColor: "roi-s",
      features: [
        { text: "IEX auto-bidding" },
        { text: "Load shifting intelligence" },
        { text: "API & webhook integration" },
        { text: "BESS & battery dispatch optimisation access" },
        { text: "HVAC & industrial load optimisation" },
        { text: "Solar / wind / renewable dispatch" },
        { text: "Carbon footprint tracking & reporting" },
        { text: "SLDC / RLDC integration" },
      ],
      checkColor: "#026061",
      footer: "24×7 support · 2h SLA · named engineer · dedicated onboarding",
      extraCta: { label: "What's included?", action: "compare" },
    },
  };

  const c = configs[plan];

  const handleCta = () => {
    if (plan === "Starter") onTrialOpen(plan);
    else onDemoOpen(plan);
  };

  const handleExtra = () => {
    if (c.extraCta.action === "compare") onCompareOpen();
    else alert(`Exploring ${plan} value sharing`);
  };

  return (
    <div className={`lead-formcard ${c.cardCls}`}>
      <div className="card-top">
        <div className="card-top-upper">
          <div>
            <span className={`pbadge ${c.badgeCls}`}>{c.badge}</span>
          </div>
          <div className="plan-name">{plan}</div>
          <div className="plan-tag">{c.tag}</div>
          <div className="price-wrap">
            {c.priceFrom && (
              <div className="price-from">
                {c.priceStarting ? "Starting at" : "From"}
              </div>
            )}
            <div className="price-row">
              <span className="sym">{c.sym}</span>
              <span className="amt">{c.amt}</span>
              <span className="per">{c.per}</span>
              {c.saveTag && <span className="save-tag">{c.saveTag}</span>}
            </div>
            {c.flexNote && <div className="price-flex-note">{c.flexNote}</div>}
          </div>
          <div className="pnote">{c.note}</div>
        </div>
        <div className="card-top-bottom">
          <button className={`cta ${c.ctaCls}`} onClick={handleCta}>
            {c.ctaLabel}
            <img src={whitdigoarw} alt="" />
          </button>
          <button className="cta-demo" onClick={handleExtra}>
            {c.extraCta.label}
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="meter-pill">
          <img src={avgicn} alt="" />
          {c.meter}
        </div>
        <div className="sec-lbl">{c.sectionLabel}</div>
        {c.features.map((f, i) => (
          <div className="feat" key={i}>
            <CheckIcon color={c.checkColor} />
            <span className="ft">
              {f.text}
              {f.sub && <small>{f.sub}</small>}
            </span>
          </div>
        ))}
        {/* <div className="div" /> */}
        <div className="roi-strip">
          <div className="roi-strip-inner">
            <div className="roi-label">Typical annual savings</div>
            <div className={`roi-val ${c.roiColor}`}>{savings[plan]}</div>
          </div>
        </div>
      </div>
      <div className="card-foot">{c.footer}</div>
    </div>
  );
}

// ── TrialForm ─────────────────────────────────────────────
function TrialForm({ plan, onSuccess }) {
  const [fields, setFields] = useState({
    fname: "",
    lname: "",
    email: "",
    company: "",
    phone: "",
    bill: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setFields((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: false }));
  };

  const validate = () => {
    const e = {};
    if (!fields.fname) e.fname = true;
    if (!fields.lname) e.lname = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) e.email = true;
    if (!fields.company) e.company = true;
    if (fields.phone.replace(/\D/g, "").length < 7) e.phone = true;
    if (!fields.bill) e.bill = true;
    return e;
  };

  // const submit = () => {
  //   const e = validate();
  //   if (Object.keys(e).length) {
  //     setErrors(e);
  //     return;
  //   }
  //   setLoading(true);
  //   setTimeout(() => {
  //     setLoading(false);
  //     onSuccess(
  //       genTrackingId(),
  //       "You're in!",
  //       "You'll be live in under 48 hours. Check your inbox for next steps.",
  //     );
  //   }, 800);
  // };
  const billMap = {
    "Under ₹3 lakh": "₹0 - ₹3,00,000",
    "₹3 – ₹10 lakh": "₹3,00,000 - ₹10,00,000",
    "₹10 – ₹50 lakh": "₹10,00,000 - ₹50,00,000",
    "₹50 lakh – ₹1 crore": "₹50,00,000 - ₹1,00,00,000",
    "Above ₹1 crore": "₹1,00,00,000+",
  };
  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        form_type: "FREE_TRIAL",
        first_name: fields.fname,
        last_name: fields.lname,
        email: fields.email,
        company_name: fields.company,
        phone: fields.phone,
        trial_details: {
          // monthly_bill_range: fields.bill,
          monthly_bill_range: billMap[fields.bill],
        },
      };

      const res = await fetch(
        "https://7gh7bc7ar7.execute-api.ap-south-1.amazonaws.com/forms/submit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        throw new Error("Failed to submit form");
      }

      const data = await res.json();

      setLoading(false);

      onSuccess(
        // genTrackingId(),
        data.tracking_id,
        "You're in!",
        "You'll be live in under 48 hours. Check your inbox for next steps."
      );
    } catch (err) {
      console.error(err);
      setLoading(false);
      // alert("Something went wrong. Please try again.");
      console.log(err, 'form error')
    }
  };

  // const inp = (id, type, ph, label, value) => (
  //   <div className="form-group">
  //     <label className="req">{label}</label>
  //     <input type={type} placeholder={ph} value={value}
  //       onChange={e => set(id, e.target.value)}
  //       className={errors[id] ? "err" : ""} />
  //     {errors[id] && <span className="field-err">This field is required</span>}
  //   </div>
  // );

  const inp = (id, type, ph, label, value) => (
    <div className="form-group">
      <label className="req">{label}</label>

      <input
        type={type}
        inputMode={id === "phone" ? "numeric" : undefined}
        placeholder={ph}
        value={value}
        onKeyDown={(e) => {
          if (id !== "phone") return;

          const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
          ];

          if (allowedKeys.includes(e.key)) return;

          if (/^\d$/.test(e.key)) return;

          if (
            e.key === "+" &&
            e.target.selectionStart === 0 &&
            !e.target.value.includes("+")
          )
            return;

          e.preventDefault();
        }}
        onChange={(e) => {
          let val = e.target.value;

          if (id === "phone") {
            val = val
              .replace(/[^\d+]/g, "") // remove alphabets
              .replace(/(?!^)\+/g, "") // only one + at start
              .slice(0, 13);
          }

          set(id, val);
        }}
        className={errors[id] ? "err" : ""}
      />

      {errors[id] && <span className="field-err">This field is required</span>}
    </div>
  );

  return (
    <div className="form-body">
      <div className="form-row">
        {inp("fname", "text", "Arjun", "First name", fields.fname)}
        {inp("lname", "text", "Sharma", "Last name", fields.lname)}
      </div>
      {inp("email", "email", "arjun@company.com", "Work email", fields.email)}
      {inp(
        "company",
        "text",
        "Acme Industries Pvt. Ltd.",
        "Company name",
        fields.company,
      )}
      {inp("phone", "tel", "+91 98765 43210", "Phone number", fields.phone)}
      <div className="form-group">
        <label className="req">Monthly electricity bill (approx.)</label>
        <select
          value={fields.bill}
          onChange={(e) => set("bill", e.target.value)}
          className={errors.bill ? "err" : ""}
        >
          <option value="" disabled hidden>
            Select range
          </option>
          <option>Under ₹3 lakh</option>
          <option>₹3 – ₹10 lakh</option>
          <option>₹10 – ₹50 lakh</option>
          <option>₹50 lakh – ₹1 crore</option>
          <option>Above ₹1 crore</option>
        </select>
        {errors.bill && (
          <span className="field-err">This field is required</span>
        )}
      </div>
      <button
        className="submit-btn submit-dark"
        onClick={submit}
        disabled={loading}
      >
        {loading ? "Submitting…" : "Claim my 45-day free trial \u00A0→"}
      </button>
      <p className="form-note">Live in 48 hours · Cancel anytime</p>
    </div>
  );
}

// ── DemoForm ──────────────────────────────────────────────
function DemoForm({ plan, onSuccess }) {
  const today = new Date().toISOString().split("T")[0];
  const [fields, setFields] = useState({
    fname: "",
    lname: "",
    email: "",
    company: "",
    phone: "",
    date: "",
    slot: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setFields((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: false }));
  };

  const validate = () => {
    const e = {};
    if (!fields.fname) e.fname = true;
    if (!fields.lname) e.lname = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) e.email = true;
    if (!fields.company) e.company = true;
    if (fields.phone.replace(/\D/g, "").length < 7) e.phone = true;
    if (!fields.date) e.date = true;
    if (!fields.slot) e.slot = true;
    return e;
  };

  // const submit = () => {
  //   const e = validate();
  //   if (Object.keys(e).length) {
  //     setErrors(e);
  //     return;
  //   }
  //   setLoading(true);
  //   setTimeout(() => {
  //     setLoading(false);
  //     onSuccess(
  //       genTrackingId(),
  //       "Demo booked!",
  //       "Our team will confirm your slot within 2 hours.",
  //     );
  //   }, 800);
  // };

  const formTypeMap = {
    starter: "STARTER_DEMO",
    growth: "GROWTH_DEMO",
    pro: "PRO_DEMO",
    max: "MAX_DEMO",
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        form_type: "PRO_DEMO",
        // form_type:formTypeMap[plan?.toLowerCase().trim()] || "PRO_DEMO",
        first_name: fields.fname,
        last_name: fields.lname,
        email: fields.email,
        company_name: fields.company,
        phone: fields.phone,
        demo_details: {
          preferred_date: fields.date,
          preferred_time_slot: fields.slot,
          plan_type: plan.toUpperCase(), // IMPORTANT
        },
      };

      const res = await fetch(
        "https://7gh7bc7ar7.execute-api.ap-south-1.amazonaws.com/forms/submit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        throw new Error("Failed to submit demo form");
      }

      const data = await res.json();

      setLoading(false);

      onSuccess(
        // genTrackingId(),
        data.tracking_id,
        "Demo booked!",
        "Our team will confirm your slot within 2 hours."
      );
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  const planColors = { Growth: "#026061", Pro: "#026061", Max: "#026061" };
  const btnColor = planColors[plan] || "#2d5f4e";

  // const inp = (id, type, ph, label, value) => (
  //   <div className="form-group">
  //     <label className="req">{label}</label>
  //     <input type={type} placeholder={ph} value={value}
  //       onChange={e => set(id, e.target.value)}
  //       className={errors[id] ? "err" : ""} />
  //     {errors[id] && <span className="field-err">This field is required</span>}
  //   </div>
  // );

  const inp = (id, type, ph, label, value) => (
    <div className="form-group">
      <label className="req">{label}</label>

      <input
        type={type}
        inputMode={id === "phone" ? "numeric" : undefined}
        placeholder={ph}
        value={value}
        onKeyDown={(e) => {
          if (id !== "phone") return;

          const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
          ];

          if (allowedKeys.includes(e.key)) return;

          if (/^\d$/.test(e.key)) return;

          e.preventDefault(); // block everything else
        }}
        onChange={(e) => {
          let val = e.target.value;

          if (id === "phone") {
            val = val.replace(/\D/g, "").slice(0, 10); // only numbers, max 10 digits
          }

          set(id, val);
        }}
        className={errors[id] ? "err" : ""}
      />

      {errors[id] && <span className="field-err">This field is required</span>}
    </div>
  );

  return (
    <div className="form-body">
      <div className="form-row">
        {inp("fname", "text", "Arjun", "First name", fields.fname)}
        {inp("lname", "text", "Sharma", "Last name", fields.lname)}
      </div>
      {inp("email", "email", "arjun@company.com", "Work email", fields.email)}
      <div className="form-row">
        {inp("company", "text", "Acme Industries", "Company", fields.company)}
        {inp("phone", "tel", "+91 98765 43210", "Phone number", fields.phone)}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="req">Preferred date</label>
          <input
            type="date"
            min={today}
            value={fields.date}
            onChange={(e) => set("date", e.target.value)}
            className={errors.date ? "err" : ""}
          />
          {errors.date && (
            <span className="field-err">This field is required</span>
          )}
        </div>
        <div className="form-group">
          <label className="req">Preferred demo slot</label>
          <select
            value={fields.slot}
            onChange={(e) => set("slot", e.target.value)}
            className={errors.slot ? "err" : ""}
          >
            <option value="" disabled hidden>
              Choose a time
            </option>
            <option>Morning (9 AM – 12 PM)</option>
            <option>Afternoon (12 PM – 4 PM)</option>
            <option>Evening (4 PM – 7 PM)</option>
          </select>
          {errors.slot && (
            <span className="field-err">This field is required</span>
          )}
        </div>
      </div>
      <button
        className="submit-btn"
        style={{ background: btnColor }}
        onClick={submit}
        disabled={loading}
      >
        {loading ? "Submitting…" : "Book my demo \u00A0→"}
      </button>
      <p className="form-note">
        Our team will confirm within 2 hours · Free · No obligation
      </p>
    </div>
  );
}

// ── MainModal ─────────────────────────────────────────────
function MainModal({ modal, onClose }) {
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setSuccess(null);
  }, [modal]);

  if (!modal) return null;

  const { type, plan } = modal;
  const colors = {
    Starter: "#026061",
    Growth: "#026061",
    Pro: "#026061",
    Max: "#026061",
  };
  const bgColors = {
    Starter: "#E0F2F2",
    Growth: "#E0F2F2",
    Pro: "#E0F2F2",
    Max: "#E0F2F2",
  };
  const titles = {
    trial: "Start your 45-day free trial",
    Growth: "Book a free Growth demo",
    Pro: "Book a free Pro demo",
    Max: "Book a free Max demo",
  };
  const subs = {
    trial: "Get live in under 48 hours.",
    Growth: "See Urjā in action across your sites.",
    Pro: "We'll walk you through AI forecasting & IEX access live.",
    Max: "Our enterprise team will map your full portfolio.",
  };

  const title = type === "trial" ? titles.trial : titles[plan] || "Book a demo";
  const sub =
    type === "trial"
      ? subs.trial
      : subs[plan] || "Our team will be in touch shortly.";
  const borderColor = type === "trial" ? "#1c1c1c" : colors[plan] || "#2d5f4e";
  const badgeLabel =
    type === "trial"
      ? "45-day free trial"
      : plan === "Pro"
        ? "Pro Plan"
        : `${plan} Plan`;

  const handleSuccess = (id, t, msg) => setSuccess({ id, title: t, msg });

  return (
    <div className="modal-overlay form-modal" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div
          className="modal-header"
          style={{ borderBottomColor: borderColor }}
        >
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
          <div
            className="modal-plan-badge"
            style={{ background: bgColors[plan], color: colors[plan] }}
          >
            {badgeLabel}
          </div>
          <div className="modal-title">{title}</div>
          <div className="modal-sub">{sub}</div>
        </div>
        <div className="modal-body">
          {success ? (
            <div className="success-state">
              <div className="success-emoji">🎉</div>
              <div className="success-title">{success.title}</div>
              <div className="tracking-box">
                <div className="tracking-label">Your Tracking ID</div>
                <div className="tracking-id">{success.id}</div>
                <div className="tracking-note">Save this for your records</div>
              </div>
              <div className="success-msg">{success.msg}</div>
              <div className="success-email-note">
                An acknowledgement has been sent to your email.
              </div>
              <button className="close-btn" onClick={onClose} style={{ marginTop: '20px' }}>
                Close
              </button>
            </div>
          ) : type === "trial" ? (
            <TrialForm plan={plan} onSuccess={handleSuccess} />
          ) : (
            <DemoForm plan={plan} onSuccess={handleSuccess} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Plan Finder ───────────────────────────────────────────
const pfPlanMap = {
  "Under ₹3 lakh": {
    plan: "Starter",
    icon: "🌱",
    tag: "Energy visibility",
    saving: "Est. ₹25,000+ saved / yr",
  },
  "₹3 – ₹10 lakh": {
    plan: "Growth",
    icon: "📈",
    tag: "Multi-site intelligence",
    saving: "Est. ₹2,10,000+ saved / yr",
  },
  "₹10 – ₹50 lakh": {
    plan: "Pro",
    icon: "⚡",
    tag: "AI-powered energy intelligence",
    saving: "Est. ₹10,50,000+ saved / yr",
  },
  "₹50 lakh – ₹1 crore": {
    plan: "Pro",
    icon: "⚡",
    tag: "AI-powered energy intelligence",
    saving: "Est. ₹42,00,000+ saved / yr",
  },
  "Above ₹1 crore": {
    plan: "Max",
    icon: "🏆",
    tag: "Autonomous optimisation + trading",
    saving: "Est. ₹84,00,000+ saved / yr",
  },
};

const PF_STEPS = [
  {
    q: "What best describes your organisation?",
    hint: "Helps us tailor the feature set and savings estimate.",
    key: "industry",
    opts: [
      "Manufacturing / Process Plant",
      "Data Centre / IT Park",
      "Commercial Real Estate",
      "Utility / DISCOM",
      "Renewable / IPP",
      "Other",
    ],
  },
  {
    q: "What is your approximate monthly electricity bill?",
    hint: "This determines your potential savings and the right plan.",
    key: "bill",
    opts: [
      "Under ₹3 lakh",
      "₹3 – ₹10 lakh",
      "₹10 – ₹50 lakh",
      "₹50 lakh – ₹1 crore",
      "Above ₹1 crore",
    ],
  },
  {
    q: "How many metering points or sites do you operate?",
    hint: "Multi-site operators unlock consolidated analytics.",
    key: "meters",
    opts: ["1 meter", "2 – 5 meters", "6 – 20 meters", "More than 20 meters"],
  },
  {
    q: "What is your primary goal with an EMS?",
    hint: "We'll highlight the features most relevant to you.",
    key: "goal",
    opts: [
      "Reduce my electricity bill",
      "Buy power smarter via IEX",
      "Visibility & compliance reporting",
      "Automate operations end-to-end",
      "ESG / carbon footprint targets",
    ],
  },
  {
    q: "When are you looking to get started?",
    hint: "We'll slot you into the right onboarding queue.",
    key: "timeline",
    opts: [
      "Immediately — this week",
      "Within 30 days",
      "This quarter",
      "Just exploring right now",
    ],
  },
];

function PlanFinder({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [cbFields, setCbFields] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    time: "",
  });
  const today = new Date().toISOString().split("T")[0];
  const [cbErrors, setCbErrors] = useState({});
  const [cbLoading, setCbLoading] = useState(false);

  const validateCallback = () => {
    const e = {};

    if (!cbFields.fname.trim()) e.fname = true;
    if (!cbFields.lname.trim()) e.lname = true;
    if (cbFields.phone.replace(/\D/g, "").length < 10) e.phone = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cbFields.email)) e.email = true;
    if (!cbFields.date) e.date = true;
    if (!cbFields.time) e.time = true;
    return e;
  };

  const submitCallback = async () => {
    const e = validateCallback();

    if (Object.keys(e).length) {
      setCbErrors(e);
      return;
    }

    setCbLoading(true);

    try {
      const payload = {
        form_type: "FIND_MY_PLAN",
        first_name: cbFields.fname,
        last_name: cbFields.lname,
        email: cbFields.email,
        phone: cbFields.phone,
        recommendation_details: {
          organization_type: answers.industry,
          monthly_bill_range: answers.bill?.replace("–", "-"),
          metering_points: answers.meters,
          ems_goal: answers.goal,
          start_timeline: answers.timeline,
          recommended_plan: rec.plan.toUpperCase(),
        },
        demo_details: {
          preferred_date: cbFields.date,
          preferred_time_slot: cbFields.time,
        },
      };

      const res = await fetch(
        "https://7gh7bc7ar7.execute-api.ap-south-1.amazonaws.com/forms/submit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Submit failed");

      await res.json();

      setCbLoading(false);
      setConfirmed(true);
    } catch (err) {
      console.error(err);
      setCbLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    if (open) {
      setStep(0);
      setAnswers({});
      setConfirmed(false);
    }
  }, [open]);

  if (!open) return null;

  const pickOpt = (key, val) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    setTimeout(() => setStep((s) => s + 1), 320);
  };

  const rec = pfPlanMap[answers.bill] || {
    plan: "Pro",
    icon: "⚡",
    tag: "AI-powered energy intelligence",
    saving: "Est. ₹10,50,000+ saved / yr",
  };
  const progress = step >= 5 ? 100 : ((step + 1) / 5) * 100;

  return (
    <div className="modal-overlay pf-overlay" onClick={onClose}>
      <div className="pf-box" onClick={(e) => e.stopPropagation()}>
        <div className="pf-progress-bar">
          <div className="pf-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="pf-topbar">
          <span className="pf-step-lbl">
            {step < 5
              ? `Step ${step + 1} of 5`
              : confirmed
                ? "All done!"
                : "Your Recommendation"}
          </span>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="pf-slides">
          {step < 5 &&
            (() => {
              const s = PF_STEPS[step];
              return (
                <div className="pf-slide">
                  <div className="pf-q">{s.q}</div>
                  <div className="pf-hint">{s.hint}</div>
                  <div className="pf-opts">
                    {s.opts.map((o, i) => (
                      <div
                        key={i}
                        className={`pf-opt ${answers[s.key] === o ? "selected" : ""}`}
                        onClick={() => pickOpt(s.key, o)}
                      >
                        {o}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

          {step >= 5 && !confirmed && (
            <div className="pf-result">
              <div className="pf-result-hero">
                {/* <div className="pf-result-icon">{rec.icon}</div> */}
                <div className="pf-result-icon">
                  <img src={avgicn} alt="" />
                </div>
                <div className="pf-result-plan-lbl">Your recommended plan</div>
                <div className="pf-result-plan">{rec.plan} Plan</div>
                <div className="pf-result-tag">{rec.tag}</div>
                <div className="pf-saving-badge">{rec.saving}</div>
              </div>
              <div className="pf-callback">
                <div className="sec-lbl">Arrange a callback</div>
                <div className="form-row gapinbwrow">
                  <div className="form-group">
                    <label className="req">First name</label>
                    <input
                      type="text"
                      value={cbFields.fname}
                      onChange={(e) => {
                        setCbFields((f) => ({ ...f, fname: e.target.value }));
                        setCbErrors((er) => ({ ...er, fname: false }));
                      }}
                      className={cbErrors.fname ? "err" : ""}
                    />
                    {cbErrors.fname && <span className="field-err">Required</span>}
                  </div>
                  <div className="form-group">
                    <label className="req">Last name</label>
                    <input
                      type="text"
                      value={cbFields.lname}
                      onChange={(e) => {
                        setCbFields((f) => ({ ...f, lname: e.target.value }));
                        setCbErrors((er) => ({ ...er, lname: false }));
                      }}
                      className={cbErrors.lname ? "err" : ""}
                    />
                    {cbErrors.lname && <span className="field-err">Required</span>}
                  </div>
                </div>
                <div className="form-row gapinbwrow">
                  <div className="form-group">
                    <label className="req">Phone number</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={cbFields.phone}
                      onChange={(e) => {
                        let val = e.target.value
                          .replace(/[^\d+]/g, "")
                          .replace(/(?!^)\+/g, "")
                          .slice(0, 13);

                        setCbFields((f) => ({ ...f, phone: val }));
                        setCbErrors((er) => ({ ...er, phone: false }));
                      }}
                      className={cbErrors.phone ? "err" : ""}
                    />
                    {cbErrors.phone && <span className="field-err">Enter valid number</span>}
                  </div>
                  <div className="form-group">
                    <label className="req">Work email</label>
                    <input
                      type="email"
                      placeholder="arjun@company.com"
                      value={cbFields.email}
                      className={cbErrors.email ? "err" : ""}
                      onChange={(e) =>
                        setCbFields((f) => ({ ...f, email: e.target.value }))
                      }
                    />
                    {cbErrors.email && <span className="field-err">Enter valid email</span>}
                  </div>
                </div>

                <div className="form-row gapinbwrow">
                  <div className="form-group">
                    <label className="req">Preferred date</label>
                    <input
                      type="date"
                      min={today}
                      value={cbFields.date}
                      className={cbErrors.date ? "err" : ""}
                      onChange={(e) =>
                        setCbFields((f) => ({ ...f, date: e.target.value }))
                      }
                    />
                    {cbErrors.date && <span className="field-err">Choose valid date</span>}
                  </div>
                  <div className="form-group">
                    <label className="req">Preferred time</label>
                    <select
                      value={cbFields.time}
                      onChange={(e) =>
                        setCbFields((f) => ({ ...f, time: e.target.value }))
                      }
                      className={cbErrors.time ? "err" : ""}
                    >
                      <option value="" disabled>
                        Choose slot
                      </option>
                      {[
                        "9:00 – 10:00 AM",
                        "10:00 – 11:00 AM",
                        "11:00 AM – 12:00 PM",
                        "12:00 – 1:00 PM",
                        "2:00 – 3:00 PM",
                        "3:00 – 4:00 PM",
                        "4:00 – 5:00 PM",
                        "5:00 – 6:00 PM",
                      ].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                    {cbErrors.time && <span className="field-err">Choose Correct time</span>}
                  </div>
                </div>
                <button
                  className="submit-btn submit-green"
                  // onClick={() => setConfirmed(true)}
                  onClick={submitCallback}
                  disabled={cbLoading}
                >
                  Confirm callback &nbsp; →
                </button>
              </div>
              <p className="form-note" style={{ textAlign: "center" }}>
                Our team calls within 2 hours during business hours · Free
                consultation
              </p>
            </div>
          )}

          {confirmed && (
            <div className="pf-confirmed">
              <div style={{ fontSize: 52, marginBottom: 14 }}>📞</div>
              <div className="pf-confirmed-title">Callback confirmed!</div>
              <div className="pf-confirmed-msg">
                We'll call you at your requested time. A calendar invite is on
                its way to your email.
                <br />
                <br />
                <strong style={{ color: "#026061" }}>
                  Your recommended plan is ready whenever you are.
                </strong>
              </div>
              <div className="pf-confirmed-btns">
                <button className="close-btn" onClick={onClose}>
                  Close
                </button>
                <button className="close-btn dark" onClick={onClose}>
                  View my plan ↗
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CompareModal ──────────────────────────────────────────
const CMP_DATA = [
  { section: "Platform & Access" },
  {
    feat: "Meters supported",
    s: "1",
    g: "Up to 5",
    p: "Up to 20",
    m: "Unlimited",
  },
  { feat: "Users", s: "3", g: "10", p: "Unlimited", m: "Unlimited" },
  {
    feat: "Historical data",
    s: "3 months",
    g: "12 months",
    p: "3 years",
    m: "Unlimited",
  },
  { feat: "Mobile & web app", s: true, g: true, p: true, m: true },
  {
    feat: "Onboarding time",
    s: "48 hrs",
    g: "48 hrs",
    p: "48 hrs",
    m: "48 hrs",
  },
  { section: "Metering & Monitoring" },
  { feat: "Real-time energy dashboard", s: true, g: true, p: true, m: true },
  { feat: "Consumption alerts & reports", s: true, g: true, p: true, m: true },
  { feat: "Meter data ingestion (MDM)", s: true, g: true, p: true, m: true },
  {
    feat: "ABT & smart meter visualisation",
    s: false,
    g: true,
    p: true,
    m: true,
  },
  { feat: "Multi-meter consolidation", s: false, g: true, p: true, m: true },
  {
    feat: "Load profiling & waste detection",
    s: false,
    g: true,
    p: true,
    m: true,
  },
  { section: "Analytics & Intelligence" },
  { feat: "Peak demand analysis", s: false, g: true, p: true, m: true },
  { feat: "Tariff dashboard", s: false, g: true, p: true, m: true },
  {
    feat: "Advanced analytics & benchmarking",
    s: false,
    g: true,
    p: true,
    m: true,
  },
  {
    feat: "IEX market prices (read-only)",
    s: false,
    g: true,
    p: true,
    m: true,
  },
  {
    feat: "AI price prediction",
    s: false,
    g: false,
    p: true,
    m: true,
    note: "ML-based IEX forecasting",
  },
  {
    feat: "AI demand forecasting",
    s: false,
    g: false,
    p: true,
    m: true,
    note: "Predict load for optimal buying",
  },
  {
    feat: "Market segment recommendation AI",
    s: false,
    g: false,
    p: true,
    m: true,
  },
  { section: "Power Procurement & Trading" },
  { feat: "Manual IEX bidding", s: false, g: false, p: true, m: true },
  { feat: "Power exchange integration", s: false, g: false, p: true, m: true },
  {
    feat: "IEX auto-bidding",
    s: false,
    g: false,
    p: false,
    m: true,
    note: "Autonomous trading engine",
  },
  { feat: "Load shifting intelligence", s: false, g: false, p: false, m: true },
  { section: "Optimisation & Dispatch" },
  { feat: "BESS & battery dispatch", s: false, g: false, p: false, m: true },
  {
    feat: "HVAC & industrial load optimisation",
    s: false,
    g: false,
    p: false,
    m: true,
  },
  {
    feat: "Solar / wind / renewable dispatch",
    s: false,
    g: false,
    p: false,
    m: true,
  },
  { feat: "Carbon footprint tracking", s: false, g: false, p: false, m: true },
  { feat: "SLDC / RLDC integration", s: false, g: false, p: false, m: true },
  { section: "Integration & Compliance" },
  { feat: "API & webhook integration", s: false, g: false, p: false, m: true },
  { feat: "SCADA / IoT integration", s: false, g: false, p: true, m: true },
  { section: "Support & SLA" },
  {
    feat: "Support channel",
    s: "Email",
    g: "Chat + email",
    p: "Priority",
    m: "24×7 dedicated",
  },
  { feat: "Response SLA", s: "72 hrs", g: "24 hrs", p: "8 hrs", m: "2 hrs" },
  { feat: "Dedicated CSM", s: false, g: false, p: true, m: true },
  { feat: "Named engineer", s: false, g: false, p: false, m: true },
  { feat: "Dedicated onboarding", s: false, g: false, p: false, m: true },
  { section: "Billing" },
  { feat: "Free trial", s: "45 days", g: false, p: false, m: false },
  { feat: "Annual discount", s: "10%", g: "10%", p: "10%", m: "10%" },
  { feat: "Value-share option", s: false, g: false, p: true, m: true },
];

function CompareModal({ open, onClose, onTrialOpen, onDemoOpen }) {
  if (!open) return null;
  const cell = (v) => {
    if (v === true)
      return (
        <td className="cmp-cell">
          <span className="cmp-yes">
            <img src={righttickicn} alt="" className="comparerwicn" />
          </span>
        </td>
      );
    if (v === false)
      return (
        <td className="cmp-cell">
          <span className="cmp-no">
            <img src={minusicn} alt="" className="comparerwicn" />
          </span>
        </td>
      );
    return (
      <td className="cmp-cell">
        <span className="cmp-val">{v}</span>
      </td>
    );
  };
  return (
    <div className="modal-overlay cmp-overlay" onClick={onClose}>
      <div className="cmp-box" onClick={(e) => e.stopPropagation()}>
        <div className="cmp-header">
          <div>
            <div className="modal-title">Full feature comparison</div>
            <div className="modal-sub">
              Everything across all four plans, side by side.
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="cmp-scroll">
          <table className="cmp-table">
            <thead>
              <tr>
                <th className="cmp-feat-col">Feature</th>
                <th>
                  <div className="cmp-plan-name">Starter</div>
                  <div className="cmp-plan-price">Free</div>
                </th>
                <th>
                  <div className="cmp-plan-name growth">Growth</div>
                  <div className="cmp-plan-price">₹6,000/mo</div>
                </th>
                <th className="col-pro">
                  <div className="cmp-plan-name pro">Pro</div>
                  <div className="cmp-plan-price">From ₹18,000/mo</div>
                </th>
                <th className="col-max">
                  <div className="cmp-plan-name max">Max</div>
                  <div className="cmp-plan-price">From ₹50,000/mo</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {CMP_DATA.map((row, i) =>
                row.section ? (
                  <tr key={i} className="cmp-section-hd">
                    <td colSpan="5">{row.section}</td>
                  </tr>
                ) : (
                  <tr key={i} className="cmp-feat-row">
                    <td className="cmp-feat-name">
                      {row.feat}
                      {row.note && <small>{row.note}</small>}
                    </td>
                    {cell(row.s)}
                    {cell(row.g)}
                    {cell(row.p)}
                    {cell(row.m)}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
        <div className="cmp-footer">
          <div className="cmpfoot-left">
            <button className="close-btn" onClick={onClose}>
              Close
            </button>
          </div>
          <div className="cmp-foot-right">
            <button
              className="close-btn dark"
              onClick={() => {
                onClose();
                onTrialOpen("Starter");
              }}
            >
              Start free with Starter <img src={whitdigoarw} alt="" />
            </button>
            <button
              className="close-btn gold"
              onClick={() => {
                onClose();
                onDemoOpen("Pro");
              }}
            >
              Book a Pro demo <img src={greendigonalarw} alt="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BottomStrip ───────────────────────────────────────────
function BottomStrip() {
  return (
    <div className="bottom-strip">
      {[
        {
          icon: handshake,
          title: "No savings? No fee.",
          desc: "Our value-sharing model means we only earn when you save. Zero upfront risk for qualifying enterprise customers.",
          cta: "Learn about value-share",
        },
        {
          icon: avgicn,
          title: "Live in 48 hours",
          desc: "Our team handles integration with your existing meters, SCADA, and IoT infrastructure. Zero disruption to operations.",
          cta: "Learn about value-share",
        },
        {
          icon: liveicn,
          title: "Free energy audit",
          desc: "Book a free 30-min session with our energy experts. We'll map your current losses and show exactly how much you could save.",
          cta: "Book free audit",
        },
      ].map((b, i) => (
        <div className="bcard" key={i}>
          <div className="bcard-icon">
            <img src={b.icon} alt={b.title} />
          </div>
          <div className="bcard-title">{b.title}</div>
          <div className="bcard-desc">{b.desc}</div>
          <div className="bcard-cta" onClick={() => alert(b.title)}>
            {b.cta}
            <img src={greendigonalarw} alt="" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── App ───────────────────────────────────────────────────
export default function App() {
  const [annual, setAnnual] = useState(false);
  const [bill, setBill] = useState(2500000);
  const [rate, setRate] = useState(7);
  const [modal, setModal] = useState(null);
  const [pfOpen, setPfOpen] = useState(false);
  const [cmpOpen, setCmpOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = modal || pfOpen || cmpOpen ? "hidden" : "";
  }, [modal, pfOpen, cmpOpen]);

  const openTrial = (plan) => setModal({ type: "trial", plan });
  const openDemo = (plan) => setModal({ type: "demo", plan });
  const closeModal = () => setModal(null);

  return (
    <section className="leadform-page">
      <div className="cust-container">
        <Hero />
        <TrustBar />
        <SavingsCalc
          bill={bill}
          rate={rate}
          annual={annual}
          onBillChange={setBill}
          onRateChange={setRate}
        />
        <BillingToggle annual={annual} onChange={setAnnual} />
        <div className="lead-grid-items">
          {["Starter", "Growth", "Pro", "Max"].map((plan) => (
            <PlanCard
              key={plan}
              plan={plan}
              annual={annual}
              bill={bill}
              rate={rate}
              onTrialOpen={openTrial}
              onDemoOpen={openDemo}
              onCompareOpen={() => setCmpOpen(true)}
            />
          ))}
        </div>
        <div className="urgency">
          <span className="urg-dot" />
          45-day trial slots are limited — claim yours before they fill
        </div>
        <div className="find-bar">
          <p>
            <strong>Not sure which plan fits?</strong> Answer 5 quick questions
            — our AI recommends the right plan and shows your personalised
            savings estimate in under 60 seconds.
          </p>
          <button className="find-btn" onClick={() => setPfOpen(true)}>
            Find my plan in 60 sec
          </button>
        </div>
        <div className="compare-btn">
          <button onClick={() => setCmpOpen(true)}>
            See full feature comparison <img src={greydigonarw} alt="" />
          </button>
        </div>
        <BottomStrip />
      </div>

      <MainModal modal={modal} onClose={closeModal} />
      <PlanFinder open={pfOpen} onClose={() => setPfOpen(false)} />
      <CompareModal
        open={cmpOpen}
        onClose={() => setCmpOpen(false)}
        onTrialOpen={openTrial}
        onDemoOpen={openDemo}
      />
    </section>
  );
}
